<template>
  <div class="introspection-details">
    <!-- Status Summary -->
    <div class="status-summary" :class="statusClass">
      <div class="status-header">
        <Icon :name="statusIcon" :size="20" />
        <h4>{{ statusTitle }}</h4>
        <Badge :variant="statusBadgeVariant" size="small">{{ statusBadgeText }}</Badge>
      </div>
      <p v-if="statusMessage" class="status-message">{{ statusMessage }}</p>
    </div>

    <!-- Metrics -->
        <!-- Metrics Cards -->
    <div v-if="showMetrics" class="metrics-grid">
      <div class="metric-card">
        <div class="metric-label">Templates</div>
        <div class="metric-value">{{ response.templateCount }}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">Resolved</div>
        <div class="metric-value">{{ resolvedCount }}</div>
      </div>
      <div class="metric-card warning-metric">
        <div class="metric-label">Unresolved</div>
        <div class="metric-value">{{ unresolvedCount }}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">Substitutions</div>
        <div class="metric-value">{{ response.substitutionCount }}</div>
      </div>
      <div class="metric-card error-metric">
        <div class="metric-label">Errors</div>
        <div class="metric-value">{{ deduplicatedErrors.length }}</div>
      </div>
      <div class="metric-card warning-metric">
        <div class="metric-label">Warnings</div>
        <div class="metric-value">{{ deduplicatedWarnings.length }}</div>
      </div>
    </div>

    <div v-if="response.templateCount > 0" class="variables-section">
      <div class="section-header">
        <Icon name="brackets-curly" :size="18" />
        <h5>Template Variables</h5>
        <Badge variant="info" size="small">{{ response.variables.length }}</Badge>
      </div>

      <div class="introspection-meta">
        <span>Templates: {{ response.templateCount }}</span>
        <span>Substitutions: {{ response.substitutionCount }}</span>
        <span v-if="response.errors.length">Errors: {{ response.errors.length }}</span>
        <span v-if="response.warnings.length">Warnings: {{ response.warnings.length }}</span>
      </div>

      <div class="introspection-table-wrapper" v-if="response.variables.length">
        <table class="introspection-table">
          <thead>
            <tr>
              <th>Template</th>
              <th>Usage Path</th>
              <th>Referenced Path</th>
              <th>Usage Value</th>
              <th>Reference Value</th>
              <th>Status</th>
              <th>Warnings</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="variable in response.variables"
              :key="`${variable.templateReference}:${variable.usageJsonPath}`"
              :class="{ 'row-error': variable.errorCode, 'row-warning': !variable.errorCode && variable.warningCodes.length }"
            >
              <td><code>{{ variable.templateReference }}</code></td>
              <td><code>{{ variable.usageJsonPath }}</code></td>
              <td><code>{{ variable.referencedPath }}</code></td>
              <td>{{ variable.usageResolvedValue ?? '—' }}</td>
              <td>{{ variable.referencedResolvedValue ?? '—' }}</td>
              <td>
                <Badge v-if="variable.errorCode" variant="error" size="small">Error</Badge>
                <Badge v-else-if="variable.resolved" variant="success" size="small">Resolved</Badge>
                <Badge v-else variant="warning" size="small">Unresolved</Badge>
                <Badge v-if="variable.caseInsensitiveMatch" variant="info" size="small">CI</Badge>
              </td>
              <td>
                <template v-if="variable.errorCode">
                  <span class="error-text" :title="variable.errorMessage">{{ variable.errorCode }}</span>
                </template>
                <template v-else-if="variable.warningCodes.length">
                  <span v-for="warning in variable.warningCodes" :key="warning" class="warning-text">{{ warning }}</span>
                </template>
                <span v-else>—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Errors -->
    <div v-if="deduplicatedErrors.length > 0" class="errors-section">
      <div class="section-header">
        <Icon name="x-circle" :size="18" />
        <h5>Errors</h5>
      </div>
      <div class="error-list">
        <div v-for="(error, index) in deduplicatedErrors" :key="`error-${index}`" class="error-item">
          <div class="error-header">
            <Badge variant="error" size="small">{{ error.code }}</Badge>
            <code v-if="error.jsonPath" class="error-path">{{ error.jsonPath }}</code>
          </div>
          <p class="error-message">{{ error.message }}</p>
          <div v-if="error.templateReference" class="error-template">
            <Icon name="code" :size="14" />
            <code>{{ error.templateReference }}</code>
          </div>
          <div v-if="error.details" class="error-details">
            <small>{{ formatDetails(error.details) }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Warnings -->
    <div v-if="deduplicatedWarnings.length > 0" class="warnings-section">
      <div class="section-header">
        <Icon name="warning" :size="18" />
        <h5>Warnings</h5>
      </div>
      <div class="warning-list">
        <div v-for="(warning, index) in deduplicatedWarnings" :key="`warning-${index}`" class="warning-item">
          <div class="warning-header">
            <Badge variant="warning" size="small">{{ warning.code }}</Badge>
            <code v-if="warning.jsonPath" class="warning-path">{{ warning.jsonPath }}</code>
          </div>
          <p class="warning-message">{{ warning.message }}</p>
          <div v-if="warning.templateReference" class="warning-template">
            <Icon name="code" :size="14" />
            <code>{{ warning.templateReference }}</code>
          </div>
        </div>
      </div>
    </div>

    <!-- Collapsible Original JSON -->
    <div class="collapsible-section">
      <button 
        class="collapsible-header" 
        @click="toggleSection('original')"
        :aria-expanded="expandedSections.original"
      >
        <Icon :name="expandedSections.original ? 'caret-down' : 'caret-right'" :size="16" />
        <h5>Original Configuration</h5>
        <Badge variant="info" size="small">JSON</Badge>
      </button>
      <div v-if="expandedSections.original" class="collapsible-content">
        <MonacoEditor
          :model-value="formatJson(response.original)"
          language="json"
          :readonly="true"
          :show-toolbar="false"
          :theme="theme"
          class="json-preview-editor"
        />
      </div>
    </div>

    <!-- Collapsible Resolved JSON -->
    <div v-if="response.resolved" class="collapsible-section">
      <button 
        class="collapsible-header" 
        @click="toggleSection('resolved')"
        :aria-expanded="expandedSections.resolved"
      >
        <Icon :name="expandedSections.resolved ? 'caret-down' : 'caret-right'" :size="16" />
        <h5>Resolved Configuration</h5>
        <Badge variant="success" size="small">JSON</Badge>
      </button>
      <div v-if="expandedSections.resolved" class="collapsible-content">
        <MonacoEditor
          :model-value="formatJson(response.resolved)"
          language="json"
          :readonly="true"
          :show-toolbar="false"
          :theme="theme"
          class="json-preview-editor"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'
import { Icon, Badge } from '@/components/atoms'
import { MonacoEditor } from '@/components/organisms'
import type { TemplateIntrospection } from '@/types/configuration'

interface Props {
  response: TemplateIntrospection
  theme?: 'vs' | 'vs-dark'
}

const props = withDefaults(defineProps<Props>(), {
  theme: 'vs-dark'
})

const expandedSections = reactive({
  original: true,
  resolved: false
})

const resolvedCount = computed(() => props.response.variables.filter(variable => variable.resolved).length)
const unresolvedCount = computed(() => props.response.variables.filter(variable => !variable.resolved && !variable.errorCode).length)

// Deduplicate errors based on code, jsonPath, and templateReference
const deduplicatedErrors = computed(() => {
  const seen = new Set<string>()
  return props.response.errors.filter(error => {
    const key = `${error.code}:${error.jsonPath}:${error.templateReference || ''}`
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
})

// Deduplicate warnings based on code, jsonPath, and templateReference
const deduplicatedWarnings = computed(() => {
  const seen = new Set<string>()
  return props.response.warnings.filter(warning => {
    const key = `${warning.code}:${warning.jsonPath}:${warning.templateReference || ''}`
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
})

const toggleSection = (section: 'original' | 'resolved') => {
  expandedSections[section] = !expandedSections[section]
}

const statusClass = computed(() => {
  if (props.response.errors.length > 0) return 'status-summary--error'
  if (props.response.templateCount > 0 && unresolvedCount.value > 0) return 'status-summary--warning'
  return 'status-summary--success'
})

const statusIcon = computed(() => {
  if (props.response.errors.length > 0) return 'x-circle'
  if (props.response.templateCount > 0 && unresolvedCount.value > 0) return 'warning'
  return 'check-circle'
})

const statusTitle = computed(() => {
  if (props.response.templateCount === 0) return 'No Template Variables Found'
  if (props.response.errors.length > 0) return 'Template Introspection Found Errors'
  if (unresolvedCount.value > 0) return 'Template Introspection Found Unresolved Variables'
  return 'Template Introspection Successful'
})

const statusBadgeVariant = computed(() => {
  if (props.response.errors.length > 0) return 'error'
  if (props.response.templateCount > 0 && unresolvedCount.value > 0) return 'warning'
  return 'success'
})

const statusBadgeText = computed(() => {
  if (props.response.templateCount === 0) return 'Info'
  if (props.response.errors.length > 0) return 'Errors'
  if (unresolvedCount.value > 0) return 'Warnings'
  return 'Success'
})

const statusMessage = computed(() => {
  if (props.response.templateCount === 0) {
    return 'No template variables were found in the original configuration.'
  }

  if (props.response.errors.length > 0) {
    const errorCount = props.response.errors.length
    return `Template introspection found ${errorCount} ${errorCount === 1 ? 'error' : 'errors'} in the configuration.`
  }

  if (unresolvedCount.value > 0) {
    return `${resolvedCount.value} template ${resolvedCount.value === 1 ? 'reference was' : 'references were'} resolved and ${unresolvedCount.value} ${unresolvedCount.value === 1 ? 'remains' : 'remain'} unresolved.`
  }

  return `Successfully resolved ${resolvedCount.value} template ${resolvedCount.value === 1 ? 'reference' : 'references'}.`
})

const showMetrics = computed(() => {
  return props.response.templateCount > 0 || props.response.errors.length > 0 || props.response.warnings.length > 0
})

const formatJson = (obj: any): string => {
  try {
    return JSON.stringify(obj, null, 2)
  } catch (e) {
    return String(obj)
  }
}

const formatDetails = (details: any): string => {
  if (typeof details === 'string') return details
  try {
    return JSON.stringify(details)
  } catch (e) {
    return String(details)
  }
}
</script>

<style scoped lang="scss">
.introspection-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background: var(--vscode-editor-background);
  border: 1px solid var(--vscode-widget-border);
  border-radius: 4px;
}

.status-summary {
  padding: 16px;
  border-radius: 4px;
  border-left: 4px solid;
  
  &--success {
    background: color-mix(in srgb, var(--status-success-color, var(--vscode-testing-iconPassed)) 10%, transparent);
    border-left-color: var(--status-success-color, var(--vscode-testing-iconPassed));
  }
  
  &--error {
    background: color-mix(in srgb, var(--status-error-color, var(--vscode-testing-iconFailed)) 10%, transparent);
    border-left-color: var(--status-error-color, var(--vscode-testing-iconFailed));
  }

  &--warning {
    background: color-mix(in srgb, var(--status-warning-color, var(--vscode-testing-iconQueued, var(--vscode-editorWarning-foreground))) 10%, transparent);
    border-left-color: var(--status-warning-color, var(--vscode-testing-iconQueued, var(--vscode-editorWarning-foreground)));
  }
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  color: var(--vscode-editor-foreground);
  flex-wrap: wrap;
  
  h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--vscode-editor-foreground);
  }
}

.status-message {
  margin: 0;
  font-size: 14px;
  color: var(--vscode-descriptionForeground);
  line-height: 1.5;
}

.metrics-section {
  padding: 12px;
  background: var(--vscode-editorWidget-background);
  border-radius: 4px;
}

.metric-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--vscode-editor-background);
  border: 1px solid var(--vscode-widget-border);
  border-radius: 4px;
  
  &--error {
    border-left: 3px solid var(--vscode-testing-iconFailed);
  }
  
  &--warning {
    border-left: 3px solid var(--vscode-testing-iconSkipped);
  }
}

.metric-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.metric-value {
  font-size: 20px;
  font-weight: 600;
  color: var(--vscode-editor-foreground);
  line-height: 1;
}

.metric-label {
  font-size: 11px;
  color: var(--vscode-descriptionForeground);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.errors-section,
.warnings-section,
.variables-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.introspection-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: var(--vscode-descriptionForeground);
}

.introspection-table-wrapper {
  max-height: 320px;
  overflow: auto;
  border: 1px solid var(--vscode-widget-border);
  border-radius: 4px;
}

.introspection-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  thead {
    position: sticky;
    top: 0;
    background: var(--vscode-editorGroupHeader-tabsBackground);
  }

  th,
  td {
    padding: 6px 8px;
    text-align: left;
    border-bottom: 1px solid var(--vscode-widget-border);
    vertical-align: top;
  }

  code {
    font-family: var(--vscode-editor-font-family);
    font-size: 11px;
  }
}

.row-error {
  background: var(--vscode-inputValidation-errorBackground);
}

.row-warning {
  background: var(--vscode-inputValidation-warningBackground);
}

.warning-text {
  display: inline-block;
  background: var(--vscode-editorWarning-foreground);
  color: var(--vscode-editor-background);
  padding: 0 4px;
  margin: 0 2px 2px 0;
  border-radius: 3px;
  font-size: 10px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--vscode-editor-foreground);
  
  h5 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--vscode-editor-foreground);
  }
}

.error-list,
.warning-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.error-item,
.warning-item {
  padding: 12px;
  background: var(--vscode-editorWidget-background);
  border-radius: 4px;
  border-left: 3px solid;
}

.error-item {
  border-left-color: var(--vscode-testing-iconFailed);
}

.warning-item {
  border-left-color: var(--vscode-testing-iconSkipped);
}

.error-header,
.warning-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.error-path,
.warning-path {
  font-family: var(--vscode-editor-font-family);
  font-size: 11px;
  background: var(--vscode-textCodeBlock-background);
  padding: 2px 6px;
  border-radius: 3px;
  color: var(--vscode-textPreformat-foreground);
}

.error-message,
.warning-message {
  margin: 0 0 8px 0;
  font-size: 13px;
  color: var(--vscode-editor-foreground);
  line-height: 1.5;
}

.error-template,
.warning-template {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  
  code {
    font-family: var(--vscode-editor-font-family);
    font-size: 12px;
    background: var(--vscode-textCodeBlock-background);
    padding: 4px 8px;
    border-radius: 3px;
    color: var(--vscode-textLink-foreground);
  }
}

.error-details {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--vscode-widget-border);
  
  small {
    font-size: 11px;
    color: var(--vscode-descriptionForeground);
    font-family: var(--vscode-editor-font-family);
  }
}

.collapsible-section {
  border: 1px solid var(--vscode-widget-border);
  border-radius: 4px;
  overflow: hidden;
}

.collapsible-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: var(--vscode-editorGroupHeader-tabsBackground);
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  color: var(--vscode-editor-foreground);
  
  &:hover {
    background: var(--vscode-list-hoverBackground);
  }
  
  &:focus {
    outline: 2px solid var(--vscode-focusBorder);
    outline-offset: -2px;
  }
  
  h5 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--vscode-editor-foreground);
    flex: 1;
  }
}

.status-header :deep(svg),
.section-header :deep(svg),
.collapsible-header :deep(svg) {
  color: var(--vscode-icon-foreground, currentColor);
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}

.collapsible-content {
  border-top: 1px solid var(--vscode-widget-border);
  background: var(--vscode-editor-background);
}

.json-preview-editor {
  height: min(45vh, 520px);
}
</style>
